import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Check, X, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function CalendarsDialog({ open, onClose, calendars, onChanged }) {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/field/calendars`, { name: newName.trim() });
      setNewName('');
      onChanged();
      toast.success('Calendar created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create calendar');
    }
  };

  const rename = async (id) => {
    if (!editName.trim()) return;
    try {
      await axios.patch(`${API}/field/calendars/${id}`, { name: editName.trim() });
      setEditingId(null);
      onChanged();
      toast.success('Calendar renamed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/field/calendars/${id}`);
      onChanged();
      toast.success('Calendar deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete calendar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="field-calendars-dialog">
        <DialogHeader>
          <DialogTitle>Manage Calendars</DialogTitle>
          <DialogDescription>Separate calendars for different crews or regions.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {calendars.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
              {editingId === c.id ? (
                <>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8" autoFocus />
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rename(c.id)}><Check size={14} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingId(null)}><X size={14} /></Button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm font-medium text-slate-800">{c.name}</span>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditingId(c.id); setEditName(c.name); }} data-testid={`rename-calendar-${c.id}`}><Pencil size={13} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(c.id)} data-testid={`delete-calendar-${c.id}`}><Trash2 size={13} /></Button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New calendar name" onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="new-calendar-name-input" />
          <Button onClick={add} className="bg-sky-600 hover:bg-sky-700" data-testid="add-calendar-btn"><Plus size={14} className="mr-1" /> Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CategoriesDialog({ open, onClose, calendarId, categories, onChanged }) {
  const [newLabel, setNewLabel] = useState('');
  const [newColor, setNewColor] = useState('#0ea5e9');

  const add = async () => {
    if (!newLabel.trim()) return;
    try {
      await axios.post(`${API}/field/categories`, { calendar_id: calendarId, label: newLabel.trim(), color: newColor });
      setNewLabel('');
      onChanged();
      toast.success('Category added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add category');
    }
  };

  const updateColor = async (id, color) => {
    try {
      await axios.patch(`${API}/field/categories/${id}`, { color });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update color');
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/field/categories/${id}`);
      onChanged();
      toast.success('Category deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete category');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="field-categories-dialog">
        <DialogHeader>
          <DialogTitle>Manage Categories</DialogTitle>
          <DialogDescription>Categories and colors for the active calendar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {categories.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
              <input
                type="color"
                value={c.color}
                onChange={(e) => updateColor(c.id, e.target.value)}
                className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent p-0"
                data-testid={`category-color-${c.id}`}
              />
              <span className="flex-1 text-sm font-medium text-slate-800">{c.label}</span>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(c.id)} data-testid={`delete-category-${c.id}`}><Trash2 size={13} /></Button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 items-center">
          <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="w-9 h-9 rounded cursor-pointer border-0 bg-transparent p-0 shrink-0" data-testid="new-category-color" />
          <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="New category label" onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="new-category-label-input" />
          <Button onClick={add} className="bg-sky-600 hover:bg-sky-700" data-testid="add-category-btn"><Plus size={14} className="mr-1" /> Add</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TechColorsDialog({ open, onClose, users, onChanged }) {
  const setColor = async (id, color) => {
    try {
      await axios.patch(`${API}/field/users/${id}/color`, { color });
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update color');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="field-tech-colors-dialog">
        <DialogHeader>
          <DialogTitle>Technician Colors</DialogTitle>
          <DialogDescription>Events on the calendar are colored by their assigned technician(s).</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {users.filter((u) => u.field_access !== false).map((u) => (
            <div key={u.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
              <input
                type="color"
                value={u.field_color || '#94a3b8'}
                onChange={(e) => setColor(u.id, e.target.value)}
                className="w-7 h-7 rounded cursor-pointer border-0 bg-transparent p-0"
                data-testid={`tech-color-${u.id}`}
              />
              <span className="flex-1 text-sm font-medium text-slate-800">{u.name}</span>
              <span className="text-xs text-slate-400">{u.role?.replace(/_/g, ' ')}</span>
            </div>
          ))}
          {users.length === 0 && <p className="text-sm text-slate-500 italic">No users found.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RecycleBinDialog({ open, onClose, onRestored, catById }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/field/recycle-bin`);
      setItems(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load recycle bin');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const restore = async (id) => {
    try {
      await axios.post(`${API}/field/events/${id}/restore`);
      toast.success('Event restored');
      load();
      onRestored();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="field-recycle-dialog">
        <DialogHeader>
          <DialogTitle>Recycle Bin</DialogTitle>
          <DialogDescription>Deleted events from the last 90 days.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {loading && <p className="text-sm text-slate-500">Loading...</p>}
          {!loading && items.length === 0 && <p className="text-sm text-slate-500 italic">Recycle bin is empty.</p>}
          {items.map((ev) => {
            const cat = catById[ev.category_id];
            return (
              <div key={ev.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cat?.color || '#64748b' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{ev.title}</p>
                  <p className="text-xs text-slate-500">{ev.date}{ev.end_date && ev.end_date !== ev.date ? ` → ${ev.end_date}` : ''}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => restore(ev.id)} data-testid={`restore-event-${ev.id}`}>
                  <RotateCcw size={13} className="mr-1.5" /> Restore
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
